import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useConceptGate } from '../../../context/ConceptGateContext';
import { Direction } from '../../../api/conceptgate';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import {
  DEFAULT_RISK_PERCENT,
  getStoredRiskPercent,
  persistRiskPercent,
} from './tradeDefaults';

const EditModeForm = ({ onCancel, onSave }) => {
  const { currentTrade, setTradeSpec, account, selectedInstrument } = useConceptGate();

  const [formData, setFormData] = useState({
    instrument: selectedInstrument || 'EURUSD',
    direction: Direction.LONG,
    entryPrice: '',
    stopPrice: '',
    targetPrice: '',
    riskPercent: DEFAULT_RISK_PERCENT.toString(),
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Scrubber state
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startValue = useRef(0);
  const activeField = useRef(null);

  // Initialize form with current trade data or selected instrument
  useEffect(() => {
    if (currentTrade) {
      setFormData({
        instrument: currentTrade.instrument || selectedInstrument || 'EURUSD',
        direction: currentTrade.direction || Direction.LONG,
        entryPrice: currentTrade.entryPrice?.toString() || '',
        stopPrice: currentTrade.stopPrice?.toString() || '',
        targetPrice: currentTrade.targetPrice?.toString() || '',
        riskPercent: currentTrade.riskPercent?.toString() || DEFAULT_RISK_PERCENT.toString(),
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      instrument: selectedInstrument || prev.instrument,
      riskPercent: getStoredRiskPercent().toString(),
    }));
  }, [currentTrade, selectedInstrument]);

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user types
    setErrors((prev) => ({
      ...prev,
      [field]: null,
    }));
  }, []);



  // Validate a single field, returning { message, suggestion } or null
  const validateField = useCallback((field, data = formData) => {
    const entry = parseFloat(data.entryPrice);
    const stop = parseFloat(data.stopPrice);
    const target = parseFloat(data.targetPrice);
    const risk = parseFloat(data.riskPercent);

    switch (field) {
      case 'entryPrice':
        if (!data.entryPrice || isNaN(entry)) {
          return { message: 'Entry price is required', suggestion: 'Enter your planned entry price' };
        }
        if (entry <= 0) {
          return { message: 'Entry price must be positive', suggestion: 'Check the current market price' };
        }
        return null;

      case 'stopPrice':
        if (!data.stopPrice || isNaN(stop)) {
          return { message: 'Stop price is required', suggestion: 'Place your stop-loss level' };
        }
        if (stop <= 0) {
          return { message: 'Stop price must be positive' };
        }
        if (!isNaN(entry) && data.direction === Direction.LONG && stop >= entry) {
          return { message: 'Stop must be below entry for long', suggestion: 'For a long trade, place stop below entry' };
        }
        if (!isNaN(entry) && data.direction === Direction.SHORT && stop <= entry) {
          return { message: 'Stop must be above entry for short', suggestion: 'For a short trade, place stop above entry' };
        }
        return null;

      case 'targetPrice':
        if (!isNaN(entry) && !isNaN(target)) {
          if (data.direction === Direction.LONG && target <= entry) {
            return { message: 'Target must be above entry for long', suggestion: 'Move target above your entry price' };
          }
          if (data.direction === Direction.SHORT && target >= entry) {
            return { message: 'Target must be below entry for short', suggestion: 'Move target below your entry price' };
          }
        }
        return null;

      case 'riskPercent':
        if (!data.riskPercent || isNaN(risk)) {
          return { message: 'Risk % is required', suggestion: 'Typical risk is 0.5-2% per trade' };
        }
        if (risk <= 0 || risk > 10) {
          return { message: 'Risk must be between 0.1% and 10%', suggestion: 'Most traders risk 0.5-2% per trade' };
        }
        return null;

      default:
        return null;
    }
  }, [formData]);

  const validateForm = useCallback(() => {
    const fields = ['entryPrice', 'stopPrice', 'targetPrice', 'riskPercent'];
    const newErrors = {};

    fields.forEach(field => {
      const error = validateField(field, formData);
      if (error) newErrors[field] = error;
    });

    setErrors(newErrors);
    setTouched({ entryPrice: true, stopPrice: true, targetPrice: true, riskPercent: true });
    return Object.keys(newErrors).length === 0;
  }, [formData, validateField]);

  // Validate on blur for touched fields
  const handleBlur = useCallback((field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field);
    setErrors(prev => {
      const next = { ...prev };
      if (error) {
        next[field] = error;
      } else {
        delete next[field];
      }
      return next;
    });
  }, [validateField]);

  // Handle save on Enter key
  const handleSave = useCallback(() => {
    if (!validateForm()) return;

    const tradeSpec = {
      instrument: formData.instrument,
      direction: formData.direction,
      entryPrice: parseFloat(formData.entryPrice),
      stopPrice: parseFloat(formData.stopPrice),
      targetPrice: formData.targetPrice ? parseFloat(formData.targetPrice) : null,
      riskPercent: parseFloat(formData.riskPercent),
    };

    setTradeSpec(tradeSpec);
    persistRiskPercent(tradeSpec.riskPercent);
    onSave();
  }, [formData, validateForm, setTradeSpec, onSave]);

  // Keyboard navigation - Tab through fields, Enter to save, Escape to cancel
  const handleKeyDown = useCallback((e, currentField) => {
    const fieldOrder = ['entryPrice', 'stopPrice', 'targetPrice', 'riskPercent'];
    const currentIndex = fieldOrder.indexOf(currentField);

    if (e.key === 'Enter') {
      e.preventDefault();
      // If on last field, save the form
      if (currentIndex === fieldOrder.length - 1 || currentField === 'riskPercent') {
        handleSave();
      } else {
        // Move to next field
        const nextField = fieldOrder[currentIndex + 1];
        const nextInput = document.querySelector(`input[name="${nextField}"]`);
        if (nextInput) nextInput.focus();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  }, [onCancel, handleSave]);



  // Scrubber handler - creates scrub functionality for a field
  const createScrubHandler = useCallback((field, step = 0.00001, decimals = 5) => {
    return (e) => {
      e.preventDefault();

      isDragging.current = true;
      activeField.current = field;
      startX.current = e.clientX;
      startValue.current = parseFloat(formData[field]) || 0;
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';

      const handleMouseMove = (moveE) => {
        if (!isDragging.current) return;
        const deltaX = moveE.clientX - startX.current;
        const deltaValue = deltaX * step;
        const newValue = startValue.current + deltaValue;
        const formattedValue = newValue.toFixed(decimals);

        setFormData(prev => ({
          ...prev,
          [field]: formattedValue,
        }));
        setErrors(prev => ({ ...prev, [field]: null }));
      };

      const handleMouseUp = () => {
        isDragging.current = false;
        activeField.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };
  }, [formData]);

  // Calculate preview
  const previewRR = () => {
    const entry = parseFloat(formData.entryPrice);
    const stop = parseFloat(formData.stopPrice);
    const target = parseFloat(formData.targetPrice);

    if (isNaN(entry) || isNaN(stop) || isNaN(target)) return null;

    const stopPips = Math.abs(entry - stop) / 0.0001;
    const targetPips = Math.abs(target - entry) / 0.0001;

    if (stopPips === 0) return null;

    return (targetPips / stopPips).toFixed(2);
  };

  const previewLotSize = () => {
    const entry = parseFloat(formData.entryPrice);
    const stop = parseFloat(formData.stopPrice);
    const riskPercent = parseFloat(formData.riskPercent);

    if (isNaN(entry) || isNaN(stop) || isNaN(riskPercent)) return null;

    const equity = account?.equity || 100000;
    const riskDollar = equity * (riskPercent / 100);
    const stopPips = Math.abs(entry - stop) / 0.0001;

    if (stopPips === 0) return null;

    const pipValue = 10; // EURUSD
    return (riskDollar / (stopPips * pipValue)).toFixed(2);
  };

  const rr = previewRR();
  const lots = previewLotSize();

  return (
    <div className="conceptgate-section conceptgate-edit-section">
      <div className="conceptgate-section-header">
        <EditIcon fontSize="small" />
        <span>Edit Trade</span>
        <button
          className="conceptgate-header-edit-btn"
          onClick={onCancel}
          title="Cancel"
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>

      <div className="conceptgate-section-content conceptgate-edit-content">
        {/* Direction */}
        <div className="conceptgate-form-group">
          <label>Direction</label>
          <div className="conceptgate-direction-toggle">
            <button
              className={`direction-btn ${formData.direction === Direction.LONG ? 'active long' : ''}`}
              onClick={() => handleChange('direction', Direction.LONG)}
              type="button"
            >
              Long
            </button>
            <button
              className={`direction-btn ${formData.direction === Direction.SHORT ? 'active short' : ''}`}
              onClick={() => handleChange('direction', Direction.SHORT)}
              type="button"
            >
              Short
            </button>
          </div>
        </div>

        {/* Price Fields Grid */}
        <div className="conceptgate-edit-grid">
          {/* Entry Price */}
          <div className="conceptgate-form-group">
            <label
              className="scrubber-label"
              onMouseDown={createScrubHandler('entryPrice', 0.00001, 5)}
            >
              Entry <span className="scrub-indicator">↔</span>
            </label>
            <input
              type="number"
              inputMode="decimal"
              name="entryPrice"
              step="0.00001"
              value={formData.entryPrice}
              onChange={(e) => handleChange('entryPrice', e.target.value)}
              onBlur={() => handleBlur('entryPrice')}
              onKeyDown={(e) => handleKeyDown(e, 'entryPrice')}
              placeholder="1.10450"
              className={touched.entryPrice && errors.entryPrice ? 'error' : ''}
            />
            {touched.entryPrice && errors.entryPrice && (
              <div className="form-error">
                <span>{errors.entryPrice.message}</span>
                {errors.entryPrice.suggestion && <span className="form-error-suggestion">{errors.entryPrice.suggestion}</span>}
              </div>
            )}
          </div>

          {/* Stop Price */}
          <div className="conceptgate-form-group">
            <label
              className="scrubber-label"
              onMouseDown={createScrubHandler('stopPrice', 0.00001, 5)}
            >
              Stop <span className="scrub-indicator">↔</span>
            </label>
            <input
              type="number"
              inputMode="decimal"
              name="stopPrice"
              step="0.00001"
              value={formData.stopPrice}
              onChange={(e) => handleChange('stopPrice', e.target.value)}
              onBlur={() => handleBlur('stopPrice')}
              onKeyDown={(e) => handleKeyDown(e, 'stopPrice')}
              placeholder="1.10150"
              className={touched.stopPrice && errors.stopPrice ? 'error' : ''}
            />
            {touched.stopPrice && errors.stopPrice && (
              <div className="form-error">
                <span>{errors.stopPrice.message}</span>
                {errors.stopPrice.suggestion && <span className="form-error-suggestion">{errors.stopPrice.suggestion}</span>}
              </div>
            )}
          </div>

          {/* Target Price */}
          <div className="conceptgate-form-group">
            <label
              className="scrubber-label"
              onMouseDown={createScrubHandler('targetPrice', 0.00001, 5)}
            >
              Target <span className="scrub-indicator">↔</span>
            </label>
            <input
              type="number"
              inputMode="decimal"
              name="targetPrice"
              step="0.00001"
              value={formData.targetPrice}
              onChange={(e) => handleChange('targetPrice', e.target.value)}
              onBlur={() => handleBlur('targetPrice')}
              onKeyDown={(e) => handleKeyDown(e, 'targetPrice')}
              placeholder="1.11000"
              className={touched.targetPrice && errors.targetPrice ? 'error' : ''}
            />
            {touched.targetPrice && errors.targetPrice && (
              <div className="form-error">
                <span>{errors.targetPrice.message}</span>
                {errors.targetPrice.suggestion && <span className="form-error-suggestion">{errors.targetPrice.suggestion}</span>}
              </div>
            )}
          </div>

          {/* Risk Percent */}
          <div className="conceptgate-form-group">
            <label
              className="scrubber-label"
              onMouseDown={createScrubHandler('riskPercent', 0.1, 1)}
            >
              Risk % <span className="scrub-indicator">↔</span>
            </label>
            <input
              type="number"
              inputMode="decimal"
              name="riskPercent"
              step="0.1"
              min="0.1"
              max="10"
              value={formData.riskPercent}
              onChange={(e) => handleChange('riskPercent', e.target.value)}
              onBlur={() => handleBlur('riskPercent')}
              onKeyDown={(e) => handleKeyDown(e, 'riskPercent')}
              placeholder="0.5"
              className={touched.riskPercent && errors.riskPercent ? 'error' : ''}
            />
            {touched.riskPercent && errors.riskPercent && (
              <div className="form-error">
                <span>{errors.riskPercent.message}</span>
                {errors.riskPercent.suggestion && <span className="form-error-suggestion">{errors.riskPercent.suggestion}</span>}
              </div>
            )}
          </div>
        </div>

        {/* Preview */}
        {(rr || lots) && (
          <div className="conceptgate-form-preview">
            {rr && <span>R:R <strong>{rr}</strong></span>}
            {lots && <span>Size <strong>{lots}</strong> lots</span>}
          </div>
        )}

        {/* Actions */}
        <div className="conceptgate-edit-actions">
          <button
            className="conceptgate-btn conceptgate-btn-primary"
            onClick={handleSave}
            type="button"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditModeForm;
